import { useRef } from "react";
import { useState } from "react";
import { Column, createRecord, insertRecordBySeq, ResultTable, ScanRecord } from "./ResultTable";
import { useScanQueueConsumer } from "./useScanQueueConsumer";
import { PdaScanEvent } from "./Pda";
import { parseException, showError, showRepeatedScanError, parseExceptionMessageKey } from "utils/exceptionUtils";
import { playScanFailedAoSound } from "../../../utils/scanUtils";
import { ScanInput } from "./ScanInput";
import { Input } from "antd-mobile";
import { isValidBarCode, showBarCodeError } from "../../../utils/BarCodeUtil";
import { t } from "../../../i18n";

export interface HandleScanEventResult {
    success: boolean;
    message?: string; // 失败原因，可选
}

type VehicleRelatedScanComponentProps = {
    value1Placeholder: string;
    value2Placeholder: string;
    value1ColumnTitle: string;
    value2ColumnTitle: string;
    isCheckBarCodeValid: boolean;
    handleScanEvent: (value1: string, value2: string) => Promise<HandleScanEventResult>;
    // 是否隐藏第一个输入框（例如车牌）
    hideValue1Input?: boolean;
    // 是否强制要求第一个输入框有值
    isValue1Required?: boolean;
}

export const VehicleRelatedScanComponent: React.FC<VehicleRelatedScanComponentProps> = ({
    value1Placeholder,
    value2Placeholder,
    value1ColumnTitle,
    value2ColumnTitle,
    isCheckBarCodeValid,
    handleScanEvent,
    hideValue1Input = false,
    isValue1Required = true,
}) => {
    const [successRecords, setSuccessRecords] = useState<ScanRecord[]>([]);
    const [failedRecords, setFailedRecords] = useState<ScanRecord[]>([]);
    const [value1, setValue1] = useState('');
    const [scanValue, setScanValue] = useState('');

    // 扫描执行锁
    const executingRef = useRef<{
        isExecuting: boolean;
        lastValue1: string;
        lastBarCode: string;
        lastExecuteTime: number;
    }>({
        isExecuting: false,
        lastValue1: '',
        lastBarCode: '',
        lastExecuteTime: 0
    });

    // 用 ref 保存 value1，以便在 onProcess 回调中拿到最新值
    const value1Ref = useRef(value1);
    value1Ref.current = value1;

    const { enqueueScan } = useScanQueueConsumer({
        maxConcurrency: 1,
        onProcess: async (event: PdaScanEvent) => {
            const barCode = event.code;
            const seq = event.seq;
            const currentValue1 = value1Ref.current;

            try {
                // 兼容收起键盘
                if (document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur();
                }
                if (isValue1Required && currentValue1 === '') {
                    playScanFailedAoSound();
                    showError({title: t('pda.common.error'), message: t('pda.common.fieldRequired', { field: value1Placeholder })});
                    return;
                }

                const now = Date.now();
                const executing = executingRef.current;

                // 检查是否是重复扫描（3秒内的相同value1+相同条码视为重复）
                if (executing.lastValue1 === currentValue1 &&
                    executing.lastBarCode === barCode &&
                    now - executing.lastExecuteTime < 3000) {
                    return;
                }

                // 如果当前有扫描正在执行，直接返回
                if (executing.isExecuting) {
                    return;
                }

                // 设置执行锁
                executing.isExecuting = true;
                executing.lastValue1 = currentValue1;
                executing.lastBarCode = barCode;
                executing.lastExecuteTime = now;
                setScanValue(barCode);

                try {
                    const {success, message} = await handleScanEvent(currentValue1, barCode);
                    if (success) {
                        setSuccessRecords(prev => insertRecordBySeq(prev, createRecord({value1: currentValue1, value2: barCode, seq})));
                    } else {
                        // 返回失败时，添加到失败记录
                        const failedReason = message || t('pda.common.scanFailed');
                        setFailedRecords(prev => insertRecordBySeq(prev, createRecord({value1: currentValue1, value2: barCode, failedReason, seq})));
                    }
                } catch (error) {
                    const messageKey = parseExceptionMessageKey(error);
                    let message: string;
                    if (messageKey === "errors.business.alreadyScanned") {
                        showRepeatedScanError(error);
                        const { message: errorMessage } = parseException(error);
                        message = errorMessage;
                    } else {
                        const {title, message: errorMessage} = parseException(error);
                        playScanFailedAoSound();
                        showError({title, message: errorMessage});
                        message = errorMessage;
                    }
                    setFailedRecords(prev => insertRecordBySeq(prev, createRecord({value1: currentValue1, value2: barCode, failedReason: message, seq})));
                } finally {
                    // 确保执行锁一定会被释放
                    executing.isExecuting = false;
                }
            } catch (error) {
                // 处理外层异常
                const messageKey = parseExceptionMessageKey(error);
                let message: string;
                if (messageKey === "errors.business.alreadyScanned") {
                    showRepeatedScanError(error);
                    const { message: errorMessage } = parseException(error);
                    message = errorMessage;
                } else {
                    const {title, message: errorMessage} = parseException(error);
                    playScanFailedAoSound();
                    showError({title, message: errorMessage});
                    message = errorMessage;
                }
                setFailedRecords(prev => insertRecordBySeq(prev, createRecord({value1: currentValue1, value2: barCode, failedReason: message, seq})));
                // 确保执行锁被释放
                executingRef.current.isExecuting = false;
            }
        },
    });

    const handleScan = (barCode: string) => {
        if (isCheckBarCodeValid) {
            if (isValidBarCode(barCode)) {
                enqueueScan(barCode);
            } else {
                showBarCodeError(barCode);
            }
        } else {
            enqueueScan(barCode);
        }
    };

    const successColumns: Column[] = [
        ...(hideValue1Input ? [] : [{title: value1ColumnTitle, key: 'value1'}]),
        {title: value2ColumnTitle, key: 'value2'},
        {title: t('pda.common.time'), key: 'time'}
    ];

    const failedColumns = [
        ...successColumns,
        {title: t('pda.common.failedReason'), key: 'failedReason'}
    ];

    const handleValue1Change = (value: string) => {
        setValue1(value);
        // 重置执行锁
        executingRef.current = {
            isExecuting: false,
            lastValue1: '',
            lastBarCode: '',
            lastExecuteTime: 0
        };
    };

    return (
        <div className="flex flex-col gap-2">
            {!hideValue1Input && (
                <div className="flex items-center mb-2 border rounded">
                    <Input
                        className="flex-1 p-2"
                        placeholder={value1Placeholder}
                        value={value1}
                        onChange={handleValue1Change}
                    />
                </div>
            )}
            <ScanInput placeholder={value2Placeholder} value={scanValue} onScan={handleScan} />

            <ResultTable
                successRecords={successRecords}
                failedRecords={failedRecords}
                successColumns={successColumns}
                failedColumns={failedColumns}
            />
        </div>
    );
};
